/**
 * Base camera controller. Internal – use CameraManager only.
 * Holds camera, target, input; smoothing and pitch clamping.
 */

import * as THREE from 'three';
import type { ICameraController, CameraMode, CameraTarget, CameraInputProvider, CameraState, BaseCameraConfig } from '../types/CameraTypes';

export abstract class CameraController implements ICameraController {
    protected camera: THREE.Camera;
    protected target: CameraTarget | null = null;
    protected config: BaseCameraConfig;
    protected inputProvider: CameraInputProvider | null = null;

    // Smoothing
    protected currentPosition: THREE.Vector3 = new THREE.Vector3();
    protected targetPosition: THREE.Vector3 = new THREE.Vector3();

    constructor(camera: THREE.Camera, config: BaseCameraConfig = {}) {
        this.camera = camera;
        this.config = {
            smoothing: true,
            smoothFactor: 0.1,
            minPitch: -Math.PI / 2 + 0.1,
            maxPitch: Math.PI / 2 - 0.1,
            enableCollision: false,
            ...config
        };
        this.inputProvider = config.inputProvider || null;

        this.currentPosition.copy(camera.position);
        this.targetPosition.copy(camera.position);
    }

    abstract get mode(): CameraMode;

    abstract update(deltaTime: number): void;

    /** Public API (ICameraController). */
    setInputProvider(inputProvider: CameraInputProvider): void {
        this.inputProvider = inputProvider;
    }

    getInputProvider(): CameraInputProvider | null {
        return this.inputProvider;
    }

    getCamera(): THREE.Camera {
        return this.camera;
    }

    setTarget(target: CameraTarget): void {
        this.target = target;
    }

    getTarget(): CameraTarget | null {
        return this.target;
    }

    abstract reset(): void;

    getState(): CameraState {
        const fov = this.camera instanceof THREE.PerspectiveCamera
            ? this.camera.fov
            : undefined;

        return {
            mode: this.mode,
            position: this.camera.position.clone(),
            rotation: this.camera.rotation.clone(),
            fov: fov,
            target: this.target ? this.target.position.clone() : undefined
        };
    }

    dispose(): void {
        this.target = null;
    }

    /** Used by subclasses each frame. */
    protected applySmoothPosition(_deltaTime: number): void {
        if (this.config.smoothing) {
            this.currentPosition.lerp(this.targetPosition, this.config.smoothFactor!);
            this.camera.position.copy(this.currentPosition);
        } else {
            this.camera.position.copy(this.targetPosition);
            this.currentPosition.copy(this.targetPosition);
        }
    }

    /** Clamp pitch to config min/max. */
    protected clampPitch(pitch: number): number {
        return Math.max(
            this.config.minPitch!,
            Math.min(this.config.maxPitch!, pitch)
        );
    }

    /** Normalize angle to [-PI, PI]. */
    protected normalizeAngle(angle: number): number {
        return Math.atan2(Math.sin(angle), Math.cos(angle));
    }
}

